import { api, queryString, type Items } from '@sentinel/web-core';
import type {
  PersonalDataErase,
  PersonalDataEraseResult,
  PrivacyRequest,
  PrivacyRequestCreate,
  PrivacyRequestList,
  PrivacyRequestUpdate,
  RetentionHold,
  RetentionMode,
  RetentionOverview,
  RetentionPeriodsUpdate,
} from '@sentinel/shared';

/** Privacy (S-06): GDPR requests, retention of personal data, holds, erasure on request. */
export const privacyEndpoints = {
  requests: (params: { status: 'open' | 'closed' | 'all'; page: number; pageSize: number }) =>
    api<PrivacyRequestList>(`/privacy/requests${queryString(params)}`),
  createRequest: (body: PrivacyRequestCreate) =>
    api<PrivacyRequest>('/privacy/requests', { method: 'POST', body }),
  updateRequest: (id: string, body: PrivacyRequestUpdate) =>
    api<PrivacyRequest>(`/privacy/requests/${id}`, { method: 'PATCH', body }),
  retention: () => api<RetentionOverview>('/privacy/retention'),
  setRetentionMode: (mode: RetentionMode) =>
    api<RetentionOverview>('/privacy/retention', { method: 'PUT', body: { mode } }),
  setRetentionPeriods: (body: RetentionPeriodsUpdate) =>
    api<RetentionOverview>('/privacy/retention/periods', { method: 'PUT', body }),
  /** JSON comes back parsed, CSV as text: the caller serializes and saves it. */
  exportSubject: (requestId: string, format: 'json' | 'csv') =>
    api<unknown>(`/privacy/requests/${requestId}/export?format=${format}`),
  erasePerson: (personId: string, body: PersonalDataErase = {}) =>
    api<PersonalDataEraseResult>(`/privacy/erase/persons/${personId}`, { method: 'POST', body }),
  /** The whole organization as the file to save, never parsed: one JSON, or a ZIP of CSV
   *  (Fase I, ADR 0018). */
  exportOrganization: (format: 'json' | 'zip') =>
    api<Blob>(`/privacy/organization-export?format=${format}`, { as: 'blob' }),
  holds: () => api<Items<RetentionHold>>('/privacy/holds'),
  setHold: (missionId: string, reason: string) =>
    api<RetentionHold>(`/privacy/holds/${missionId}`, { method: 'PUT', body: { reason } }),
  clearHold: (missionId: string) =>
    api<{ status: 'ok' }>(`/privacy/holds/${missionId}`, { method: 'DELETE' }),
  eraseUser: (userId: string, body: PersonalDataErase = {}) =>
    api<PersonalDataEraseResult>(`/privacy/erase/users/${userId}`, { method: 'POST', body }),
};
